import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { productionApi } from '../../services/productionApi.js';

export function MrpPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ bomId: '', quantity: '1', warehouseId: '' });
  const [planInput, setPlanInput] = useState(null);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const boms = useQuery({
    queryKey: ['production', 'boms', 'active'],
    queryFn: () => productionApi.listBoms({ activeOnly: true }).then((r) => r.items),
  });
  const warehouses = useQuery({
    queryKey: ['inventory', 'warehouses', 'for-mrp'],
    queryFn: () => inventoryApi.listWarehouses().then((r) => r.items),
  });

  const plan = useQuery({
    queryKey: ['production', 'mrp-plan', planInput],
    queryFn: () => productionApi.planMrp(planInput),
    enabled: !!planInput,
  });

  // On-hand for every "buy" component at the chosen warehouse, to net shortages.
  const buyIds = (plan.data?.buy || []).map((b) => b.componentProductId);
  const stock = useQuery({
    queryKey: ['production', 'mrp-stock', planInput?.warehouseId, buyIds.join(',')],
    enabled: !!planInput?.warehouseId && buyIds.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        buyIds.map(async (pid) => {
          const levels = await inventoryApi.stockByProduct(pid).then((r) => r.items);
          const lv = (levels || []).find((l) => l.warehouseId === planInput.warehouseId);
          return [pid, lv ? Number(lv.quantity) : 0];
        }),
      );
      return Object.fromEntries(entries);
    },
  });

  const buyRows = useMemo(() => {
    const onHand = stock.data || {};
    return (plan.data?.buy || []).map((b) => {
      const have = onHand[b.componentProductId] ?? null;
      const shortage = have === null ? null : Math.max(0, Number((b.requiredQuantity - have).toFixed(4)));
      return { ...b, onHand: have, shortage };
    });
  }, [plan.data, stock.data]);

  const run = (e) => {
    e.preventDefault();
    setPlanInput({ bomId: form.bomId, quantity: form.quantity, warehouseId: form.warehouseId || null });
  };

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('production.mrp.title')}</h1>
          <p className="muted">{t('production.mrp.subtitle')}</p>
        </div>
      </header>

      <form onSubmit={run} className="form-stack" style={{ maxWidth: '720px' }}>
        <div className="field-row">
          <div className="field" style={{ flex: 2 }}>
            <label className="field-label" htmlFor="mrp-bom">{t('production.orders.bom')}</label>
            <select id="mrp-bom" className="select" required value={form.bomId} onChange={set('bomId')}>
              <option value="">{t('common.select')}</option>
              {(boms.data || []).map((b) => (
                <option key={b.id} value={b.id}>{b.code} - {b.name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="mrp-qty">{t('production.orders.quantity')}</label>
            <input id="mrp-qty" type="number" min="0.0001" step="any" className="input" required value={form.quantity} onChange={set('quantity')} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="mrp-wh">{t('production.orders.warehouse')}</label>
            <select id="mrp-wh" className="select" value={form.warehouseId} onChange={set('warehouseId')}>
              <option value="">{t('common.none')}</option>
              {(warehouses.data || []).map((w) => (
                <option key={w.id} value={w.id}>{w.code}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="page-actions">
          <button type="submit" className="btn primary">{t('production.mrp.run')}</button>
        </div>
      </form>

      {plan.data && (
        <>
          <h2>{t('production.mrp.buy')}</h2>
          {buyRows.length === 0 ? (
            <EmptyState message={t('production.mrp.empty')} />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('production.boms.component')}</th>
                  <th className="num">{t('production.orders.required')}</th>
                  <th className="num">{t('production.mrp.onHand')}</th>
                  <th className="num">{t('production.mrp.shortage')}</th>
                </tr>
              </thead>
              <tbody>
                {buyRows.map((r) => (
                  <tr key={r.componentProductId}>
                    <td>{r.componentName || r.componentSku}</td>
                    <td className="num">{r.requiredQuantity}</td>
                    <td className="num">{r.onHand === null ? '-' : r.onHand}</td>
                    <td className="num">
                      {r.shortage === null ? '-' : r.shortage > 0
                        ? <span className="status-pill status-cancelled">{r.shortage}</span>
                        : <span className="status-pill status-completed">0</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {(plan.data.make || []).length > 0 && (
            <>
              <h2>{t('production.mrp.make')}</h2>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('production.boms.component')}</th>
                    <th className="num">{t('production.orders.required')}</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.data.make.map((m) => (
                    <tr key={m.componentProductId}>
                      <td>{m.componentName || m.componentSku}</td>
                      <td className="num">{m.requiredQuantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </section>
  );
}
